import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout_thunk } from "../redux/auth/authThunk";
import {
  get_user_chatrooms_thunk,
  get_chatroom_by_id_thunk,
  get_unread_count_thunk,
} from "../redux/chatroom/chatroomThunk";
import {
  get_initial_messages_thunk,
  get_older_messages_thunk,
  send_message_thunk,
  mark_messages_as_read_thunk,
  get_unread_message_count_thunk,
  update_message_thunk,
  upload_image_message_thunk,
} from "../redux/chat/chatThunk";
import {
  clearMessages,
  addMessage,
  updatePagination,
} from "../redux/chat/chatSlice";
import {
  updateActiveChatRoomSettings,
  updateChatRoomMediaLink,
} from "../redux/chatroom/chatroomSlice";
import socketService from "../services/socket";
import { USER_AU_TOKEN, USER_AU_USER } from "../common/key";

// Import components
import Header from "../components/chat/Header";
import Sidebar from "../components/chat/Sidebar";
import ChatHeader from "../components/chat/ChatHeader";
import Messages from "../components/chat/Messages";
import MessageInput from "../components/chat/MessageInput";

// Add this to your index.css or App.css file
// This ensures the app takes up the full screen
const fullScreenStyles = document.createElement("style");
fullScreenStyles.innerHTML = `
  html, body, #root {
    height: 100%;
    width: 100%;
    margin: 0;
    padding: 0;
    overflow: hidden;
    background-color: #111827;
    color-scheme: dark;
  }
`;
document.head.appendChild(fullScreenStyles);

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get user from localStorage instead of Redux
  const [user, setUser] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const chatRooms = useSelector((state) => state.chatroom.chatRooms);
  const messages = useSelector((state) => state.chat.messages);
  const loading = useSelector((state) => state.chat.loading);
  const chatRoomsLoading = useSelector((state) => state.chatroom.loading);

  const pagination = useSelector((state) => state.chat.pagination);
  const unreadCounts = useSelector((state) => state.chat.unreadCounts);

  const [activeChat, setActiveChat] = useState(null);
  const [message, setMessage] = useState("");
  const [showSidebar, setShowSidebar] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [unreadCount, setUnreadCount] = useState({});

  const messagesContainerRef = useRef(null);
  const messageEndRef = useRef(null);
  const previousHeightRef = useRef(null);
  const messageInputRef = useRef(null);
  const [initialScrollTop, setInitialScrollTop] = useState(0);
  const [lastLoadedBeforeId, setLastLoadedBeforeId] = useState(null);
  const [isApiCallInProgress, setIsApiCallInProgress] = useState(false);
  const scrollHandlerTimeoutRef = useRef(null);
  const loadedMessageBatchesRef = useRef({});

  // State for socket connection status
  const [socketConnected, setSocketConnected] = useState(false);

  // Add a debounce for mark as read
  const markMessagesAsReadRef = useRef(null);

  const debounceMarkAsRead = (roomId, userId) => {
    // Clear any existing timeout
    if (markMessagesAsReadRef.current) {
      clearTimeout(markMessagesAsReadRef.current);
    }

    // Set a new timeout (300ms delay to debounce multiple calls)
    markMessagesAsReadRef.current = setTimeout(() => {
      dispatch(
        mark_messages_as_read_thunk({
          roomId,
          userId,
        })
      );
      markMessagesAsReadRef.current = null;
    }, 300);
  };

  // Component setup
  useEffect(() => {
    // Load user data from localStorage
    const userDataString = localStorage.getItem(USER_AU_USER);
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        setUser(userData);

        // Initialize socket connection with user token
        const tokenData = localStorage.getItem(USER_AU_TOKEN);
        if (tokenData) {
          socketService.connectSocket(tokenData);
          setSocketConnected(true);
        }

        // Load user chat rooms
        dispatch(get_user_chatrooms_thunk());

        // Get unread counts - call without parameters
        // dispatch(get_unread_count_thunk());
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    } else {
      // No user data found, redirect to login
      navigate("/");
    }

    // Cleanup on unmount
    return () => {
      socketService.disconnectSocket();
    };
  }, [dispatch, navigate]);

  // Setup socket listeners for real-time updates
  useEffect(() => {
    if (activeChat && user?._id && socketConnected) {
      // Join the chat room
      socketService.joinChatRoom(activeChat);

      // Listen for new messages
      socketService.onNewMessage((data) => {
        if (data.message && data.chatRoomId === activeChat) {
          dispatch(addMessage(data.message));

          // Mark messages as read if not the sender
          if (data.message.sender !== user._id) {
            debounceMarkAsRead(activeChat, user._id);
          }

          // Scroll to bottom for new messages
          setTimeout(() => {
            messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
          }, 100);
        }
      });

      // Listen for edited messages
      socketService.onMessageEdited((data) => {
        if (data.messageId && data.message && data.roomId === activeChat) {
          // Log for debugging

          // Update the message in the Redux store
          dispatch({
            type: "chat/updateMessage",
            payload: {
              messageId: data.messageId,
              message: data.message.message,
              isEdited: true,
              editedAt: data.message.editedAt || new Date().toISOString(),
            },
          });

          // No need to scroll - edited messages should maintain current scroll position
        }
      });

      // Listen for admin message edits (critical for receiving edits from admin)
      socketService.onAdminMessageEdited((data) => {
        // Deep debug log to understand data structure

        // Check different possible data structures, as admin may format differently
        const messageId =
          data.messageId || data._id || (data.message && data.message._id);
        const roomId =
          data.roomId ||
          data.chatRoomId ||
          (data.message && data.message.chatRoomId);
        const messageContent =
          data.message &&
          (typeof data.message === "string"
            ? data.message
            : data.message.message);

        // Verify we have the minimum data needed
        if (messageId && roomId === activeChat && messageContent) {
          // Log for debugging

          // Find if message exists in store
          const existingMessage = messages.find((m) => m._id === messageId);
          if (existingMessage) {
          } else {
            console.warn(
              "Warning: Message to update not found in store:",
              messageId
            );
            // Try to find by partial ID match
            const partialMatch = messageId.includes("-")
              ? messages.find(
                  (m) =>
                    messageId.startsWith(m._id) || m._id.startsWith(messageId)
                )
              : null;

            if (partialMatch) {
            }
          }

          // Update the message in the Redux store
          dispatch({
            type: "chat/updateMessage",
            payload: {
              messageId,
              message: messageContent,
              isEdited: true,
              editedAt:
                (data.message && data.message.editedAt) ||
                data.editedAt ||
                new Date().toISOString(),
            },
          });

          // Dispatch a specific alert to show the user that a message was edited
        } else {
          console.error("Invalid admin edit message format:", data);
        }
      });

      // Listen for typing indicators
      socketService.onUserTyping((data) => {
        if (data.userId !== user._id) {
          setTypingUsers((prev) => ({
            ...prev,
            [data.userId]: {
              isTyping: data.isTyping,
              timestamp: new Date(),
            },
          }));
        }
      });

      // Listen for chat settings updates
      if (typeof socketService.onChatSettingsUpdated === "function") {
        socketService.onChatSettingsUpdated((data) => {
          if (data.roomId === activeChat) {
            // Update the active chat room with new settings via Redux action
            dispatch(
              updateActiveChatRoomSettings({
                roomId: data.roomId,
                settings: data.settings,
              })
            );
          }
        });
      }

      // Listen for media link submissions
      if (typeof socketService.onMediaLinkSubmitted === "function") {
        socketService.onMediaLinkSubmitted((data) => {
          if (data.roomId === activeChat) {
            // Update the chat room with the new media link
            dispatch(
              updateChatRoomMediaLink({
                roomId: data.roomId,
                mediaType: data.mediaType,
                link: data.link,
              })
            );
          }
        });
      }

      // Listen for chat room order updates
      if (typeof socketService.onChatRoomOrderUpdated === "function") {
        socketService.onChatRoomOrderUpdated((data) => {
          if (data.userId === user._id) {
            // Refresh the chat rooms to get the updated order
            dispatch(get_user_chatrooms_thunk());
          }
        });
      }

      // Listen for chat room order reset
      if (typeof socketService.onChatRoomOrderReset === "function") {
        socketService.onChatRoomOrderReset((data) => {
          if (data.userId === user._id) {
            // Refresh the chat rooms to get the default order
            dispatch(get_user_chatrooms_thunk());
          }
        });
      }

      // Clean up listeners when unmounting or changing rooms
      return () => {
        socketService.leaveChatRoom(activeChat);
      };
    }
  }, [activeChat, user, socketConnected, dispatch]);

  // Check if screen size changes
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setShowSidebar(false);
      } else {
        setShowSidebar(true);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Initial check

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // If no active chat is selected, set the first one as active
  useEffect(() => {
    // Removing automatic selection of first chatroom
    // Now no chatroom will be selected by default
    // if (chatRooms.length > 0 && !activeChat) {
    //   setActiveChat(chatRooms[0]._id);
    // }
  }, [chatRooms, activeChat]);

  // Load messages when active chat changes
  useEffect(() => {
    if (activeChat && user?._id) {
      // Reset pagination-related states
      setIsLoadingOlder(false);
      setIsApiCallInProgress(false);
      setLastLoadedBeforeId(null);
      previousHeightRef.current = null;

      // Clear any pending timeouts
      if (scrollHandlerTimeoutRef.current) {
        clearTimeout(scrollHandlerTimeoutRef.current);
        scrollHandlerTimeoutRef.current = null;
      }

      // Clear cache of loaded message batches
      loadedMessageBatchesRef.current = {};

      // Clear existing messages and loading states
      dispatch(clearMessages());

      // Fetch initial batch of messages (20 messages)
      dispatch(
        get_initial_messages_thunk({
          roomId: activeChat,
          userId: user._id,
          limit: 20,
        })
      ).then(() => {
        // After messages load, scroll to bottom with a short delay to ensure DOM is updated
        setTimeout(() => {
          if (messageEndRef.current) {
            messageEndRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 150);
      });

      // Mark messages as read when chat room is opened
      debounceMarkAsRead(activeChat, user._id);
    }
  }, [activeChat, user, dispatch]);

  // Handle scrolling to load older messages
  useEffect(() => {
    // Track the last scroll position to determine scroll direction
    const lastScrollTop = { current: 0 };

    const handleScroll = () => {
      const container = messagesContainerRef.current;
      if (!container) return;

      // Determine scroll direction
      const isScrollingUp = container.scrollTop < lastScrollTop.current;
      lastScrollTop.current = container.scrollTop;

      // If we're already loading, don't trigger another load
      if (isLoadingOlder) return;

      // Check if we have enough messages to trigger pagination (at least 18)
      if (messages.length < 18) return;

      // Only load more when scrolling UP and near the top
      if (
        isScrollingUp &&
        container.scrollTop < 200 &&
        pagination.hasMore &&
        pagination.cursors?.before &&
        pagination.cursors.before !== lastLoadedBeforeId && // Prevent duplicate loading
        !isApiCallInProgress // Prevent concurrent API calls
      ) {
        // Debounce: Clear any existing timeout
        if (scrollHandlerTimeoutRef.current) {
          clearTimeout(scrollHandlerTimeoutRef.current);
        }

        // Set a new timeout to delay the API call
        scrollHandlerTimeoutRef.current = setTimeout(() => {
          // Double-check all conditions again before proceeding
          if (
            messagesContainerRef.current &&
            pagination.hasMore &&
            pagination.cursors?.before &&
            pagination.cursors.before !== lastLoadedBeforeId &&
            !isApiCallInProgress &&
            !loadedMessageBatchesRef.current[pagination.cursors.before] // Check if this batch was already loaded
          ) {
            // Set loading state and store current scroll height
            setIsLoadingOlder(true);
            setIsApiCallInProgress(true); // Mark API call as in progress
            previousHeightRef.current = container.scrollHeight;

            // Store current scroll position in state
            setInitialScrollTop(container.scrollTop);

            // Track the before ID we're about to load
            const currentBeforeId = pagination.cursors.before;
            setLastLoadedBeforeId(currentBeforeId);

            // Mark this batch as being loaded
            loadedMessageBatchesRef.current[currentBeforeId] = true;

            console.log(
              "Loading older messages with beforeId:",
              currentBeforeId
            );

            // Dispatch the action to get older messages
            dispatch(
              get_older_messages_thunk({
                roomId: activeChat,
                userId: user._id,
                beforeId: currentBeforeId,
                limit: 20,
              })
            )
              .then((response) => {
                // Verify that we got valid data and update hasMore flag if needed
                const result = response?.payload?.data;
                if (
                  result &&
                  (!result.messages || result.messages.length === 0)
                ) {
                  // If we got no messages, update Redux to prevent further attempts
                  console.log(
                    "No more messages available, updating hasMore flag"
                  );
                  dispatch(updatePagination({ hasMore: false }));
                }

                // Short timeout to ensure DOM has updated
                setTimeout(() => {
                  // After loading older messages, maintain the scroll position
                  if (
                    messagesContainerRef.current &&
                    previousHeightRef.current
                  ) {
                    const newHeight = messagesContainerRef.current.scrollHeight;
                    const heightDiff = newHeight - previousHeightRef.current;

                    // Use requestAnimationFrame for smoother rendering
                    requestAnimationFrame(() => {
                      // Apply scroll position adjustment directly without animation
                      if (messagesContainerRef.current) {
                        messagesContainerRef.current.style.scrollBehavior =
                          "auto";
                        // Use the scroll position from state
                        messagesContainerRef.current.scrollTop =
                          heightDiff + initialScrollTop;

                        // Reset scroll behavior after applying the position
                        setTimeout(() => {
                          if (messagesContainerRef.current) {
                            messagesContainerRef.current.style.scrollBehavior =
                              "";
                          }
                        }, 10);
                      }

                      // Clear the loading state after adjusting scroll position
                      setIsLoadingOlder(false);
                      setIsApiCallInProgress(false); // Mark API call as completed
                    });
                  } else {
                    setIsLoadingOlder(false);
                    setIsApiCallInProgress(false); // Mark API call as completed
                  }
                }, 150); // Increased timeout to ensure DOM is fully updated
              })
              .catch((error) => {
                console.error("Error loading older messages:", error);
                setIsLoadingOlder(false);
                setIsApiCallInProgress(false); // Mark API call as completed even if there was an error
                // Remove from the cache on error so it can be retried later
                delete loadedMessageBatchesRef.current[currentBeforeId];
                // Clear the lastLoadedBeforeId if there was an error to allow retrying
                setLastLoadedBeforeId(null);
              });
          }
        }, 300); // 300ms debounce delay
      }
    };

    const container = messagesContainerRef.current;
    if (container) {
      // Initialize the last scroll position
      lastScrollTop.current = container.scrollTop;

      // Using passive: true for better scroll performance
      container.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        container.removeEventListener("scroll", handleScroll);
        // Clear any pending timeouts on cleanup
        if (scrollHandlerTimeoutRef.current) {
          clearTimeout(scrollHandlerTimeoutRef.current);
        }
      };
    }
  }, [
    activeChat,
    pagination,
    dispatch,
    user,
    isLoadingOlder,
    messages.length,
    lastLoadedBeforeId,
    isApiCallInProgress,
    initialScrollTop,
  ]);

  // Initialize unread counts when chat rooms are loaded
  useEffect(() => {
    if (chatRooms.length > 0) {
      const initialCounts = {};
      chatRooms.forEach((room) => {
        initialCounts[room._id] = room.unreadCount || 0;
      });
      setUnreadCount(initialCounts);
    }
  }, [chatRooms]);

  // Update unread counts when socket receives new message
  useEffect(() => {
    if (socketConnected && user?._id) {
      socketService.onNewMessage((data) => {
        if (data && data.message) {
          dispatch({
            type: "chat/addMessage",
            payload: data.message,
          });
          // Increment unread count for the room if message is not from current user
          if (data.message.sender !== user._id) {
            debounceMarkAsRead(activeChat, user._id);

            if (data?.message?.chatRoomId !== activeChat) {
              setUnreadCount((prev) => ({
                ...prev,
                [data.roomId]: (prev[data.roomId] || 0) + 1,
              }));
            }
          }
        }
      });
    }
  }, [socketConnected, user, activeChat, dispatch]);

  // Reset unread count when changing active chat
  useEffect(() => {
    if (activeChat && unreadCount[activeChat] > 0) {
      setUnreadCount((prev) => ({
        ...prev,
        [activeChat]: 0,
      }));
      dispatch(
        mark_messages_as_read_thunk({
          roomId: activeChat,
          userId: user?._id,
        })
      );
    }
  }, [activeChat, user?._id, dispatch]);

  const handleSendMessage = (e) => {
    if (e) e.preventDefault();

    if (!message.trim() || !activeChat || !user?._id) return;

    // Save the message text, only trim the beginning and end but preserve internal line breaks
    const messageText = message.trim();
    setMessage("");

    const messageData = {
      chatRoomId: activeChat,
      sender: user._id,
      senderName: `${user.firstName} ${user.lastName}`.trim() || "User",
      message: messageText,
      messageType: "text", // Ensure message type is set
      timestamp: new Date().toISOString(),
      sequence: Date.now(), // Add sequence number
    };

    // Dispatch the send message thunk which now handles:
    // 1. Adding the message locally to Redux state
    // 2. Sending via socket for real-time delivery
    // 3. Sending to API for persistence
    dispatch(send_message_thunk(messageData));

    // Scroll to bottom immediately for instant feedback
    setTimeout(() => {
      messageEndRef.current?.scrollIntoView({ behavior: "auto" });
    }, 50);
  };

  const handleLogout = () => {
    // Clear localStorage
    localStorage.removeItem(USER_AU_TOKEN);
    localStorage.removeItem(USER_AU_USER);
    // Dispatch logout action
    dispatch(logout_thunk());
    // Navigate to login
    window.location.href = "/";
  };

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  // Get unread count for a specific chat room
  const getUnreadCount = (roomId) => {
    return unreadCount[roomId] || 0;
  };

  // Format timestamp to readable time
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return "";

    try {
      // Handle different timestamp formats
      let date;
      if (typeof timestamp === "string") {
        date = new Date(timestamp);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        return "";
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "";
      }

      const now = new Date();
      const diff = now - date;

      // If less than 24 hours, show time
      if (diff < 86400000) {
        return date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      }

      // If less than 7 days, show day of week
      if (diff < 604800000) {
        return date.toLocaleDateString([], {
          weekday: "short",
        });
      }

      // Otherwise show date
      return date.toLocaleDateString([], {
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      console.error("Error formatting timestamp:", error, timestamp);
      return "";
    }
  };

  // Get active chat details
  const activeChatDetails = chatRooms.find((chat) => chat._id === activeChat);

  // Add a handler for message editing
  const handleEditMessage = (messageId, newText, roomId) => {
    if (!user || !messageId || !newText.trim() || !roomId) {
      console.error("Missing required data for editing message");
      return;
    }

    // Set a flag to prevent auto-scrolling to bottom after edit
    const shouldPreventScroll = true;

    dispatch(
      update_message_thunk({
        messageId,
        message: newText.trim(),
        roomId,
        sender: user._id,
        preventScroll: shouldPreventScroll,
      })
    );
  };

  // Maintain scroll position when loading older messages
  // This effect is now handled directly in the scroll handler

  // Handle when new messages arrive or messages are updated
  useEffect(() => {
    // Check if we're already near the bottom (within 300px) before auto-scrolling
    const isNearBottom = () => {
      if (!messagesContainerRef.current) return false;

      const { scrollTop, scrollHeight, clientHeight } =
        messagesContainerRef.current;
      const scrollPosition = scrollHeight - scrollTop - clientHeight;

      return scrollPosition < 300; // Within 300px of bottom
    };

    // Check if we should scroll to the bottom
    const shouldScrollToBottom = () => {
      // Don't scroll if we just loaded older messages (at the top)
      if (isLoadingOlder || previousHeightRef.current) {
        return false;
      }

      // Don't scroll if this is an edited message with preventScroll flag
      const recentlyEditedMessages = messages.filter(
        (msg) => msg.isEdited && msg.preventScroll
      );
      if (recentlyEditedMessages.length > 0) {
        // Clear preventScroll flag after a short delay
        setTimeout(() => {
          recentlyEditedMessages.forEach((msg) => {
            dispatch({
              type: "chat/updateMessage",
              payload: {
                messageId: msg._id,
                preventScroll: false,
              },
            });
          });
        }, 500);
        return false;
      }

      // Only scroll for new messages from the current user or if already near bottom
      return (
        isNearBottom() ||
        messages.length === 0 ||
        (messages.length > 0 &&
          messages[messages.length - 1].sender === user?._id)
      );
    };

    // Only scroll to bottom if appropriate
    if (shouldScrollToBottom()) {
      setTimeout(() => {
        messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [messages, user, dispatch, isLoadingOlder]);

  // On mobile, hide sidebar when chat is selected
  useEffect(() => {
    if (isMobile && activeChat) {
      setShowSidebar(false);
    }
  }, [activeChat, isMobile]);

  // Handle drag and drop for images
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (!activeChat || !user?._id) return;

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter((file) =>
      ["image/jpeg", "image/png", "image/gif", "image/webp"].includes(file.type)
    );

    if (imageFiles.length === 0) {
      alert("Please drop only image files (JPEG, PNG, GIF, WebP)");
      return;
    }

    // Process each image file
    imageFiles.forEach((file) => {
      // Check file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert(`Image ${file.name} is too large. Maximum size is 5MB.`);
        return;
      }

      // Create form data
      const formData = new FormData();
      formData.append("image", file);
      formData.append("chatRoomId", activeChat);
      formData.append("sender", user._id);
      formData.append(
        "senderName",
        `${user.firstName} ${user.lastName}`.trim() || "User"
      );
      formData.append("message", "Image");

      // Upload the image
      dispatch(upload_image_message_thunk(formData))
        .unwrap()
        .catch((error) => {
          console.error("Error uploading image:", error);
          alert("Failed to upload image. Please try again.");
        });
    });
  };

  return (
    <div className="h-screen w-screen flex flex-col bg-gray-900 overflow-hidden">
      <Header
        user={user}
        onLogout={handleLogout}
        isMobile={isMobile}
        onToggleSidebar={toggleSidebar}
        onEditProfile={() => navigate("/edit-profile")}
      />

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Sidebar with improved mobile handling */}
        <div
          className={`${
            showSidebar ? "translate-x-0" : "-translate-x-full"
          } transition-transform duration-300 ease-in-out fixed md:relative z-30 
          w-full md:w-72 h-[calc(100%-4rem)] md:h-full bg-[#0F172A] shadow-lg md:shadow-none 
          md:translate-x-0 flex flex-col border-r border-gray-800
          ${isMobile ? "top-16" : ""}`}
        >
          <Sidebar
            showSidebar={showSidebar}
            isMobile={isMobile}
            chatRooms={chatRooms}
            activeChat={activeChat}
            onChatSelect={setActiveChat}
            onToggleSidebar={toggleSidebar}
            getUnreadCount={getUnreadCount}
            chatRoomsLoading={chatRoomsLoading}
            unreadCount={unreadCount}
            setUnreadCount={setUnreadCount}
          />
        </div>

        {/* Overlay for mobile when sidebar is open */}
        {isMobile && showSidebar && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-20"
            onClick={() => setShowSidebar(false)}
          />
        )}

        {/* Main Chat Area with improved mobile layout */}
        <div className="flex-1 flex flex-col bg-gray-900 relative min-w-0">
          {activeChat ? (
            <>
              <ChatHeader
                activeChatDetails={activeChatDetails}
                messages={messages}
                isMobile={isMobile}
                onToggleSidebar={toggleSidebar}
              />

              <Messages
                messages={messages}
                loading={loading}
                isLoadingOlder={isLoadingOlder}
                messagesContainerRef={messagesContainerRef}
                messageEndRef={messageEndRef}
                user={user}
                formatMessageTime={formatMessageTime}
                onEditMessage={handleEditMessage}
                className="flex-1 overflow-y-auto px-2 md:px-4"
              />

              <MessageInput
                message={message}
                setMessage={setMessage}
                handleSendMessage={handleSendMessage}
                loading={loading}
                messageInputRef={messageInputRef}
                user={user}
                activeChat={activeChat}
                className="p-2 md:p-4 bg-gray-900 border-t border-gray-800"
              />

              {/* Dropbox overlay */}
              {isDragging && (
                <div className="absolute inset-0 bg-purple-900/50 flex items-center justify-center z-50">
                  <div className="bg-gray-800 p-8 rounded-lg shadow-xl text-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-16 w-16 mx-auto text-purple-400 mb-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    <p className="text-white text-lg font-medium">
                      Drop images here
                    </p>
                    <p className="text-gray-400 text-sm mt-2">
                      Supported formats: JPEG, PNG, GIF, WebP
                    </p>
                    <p className="text-gray-400 text-sm">
                      Maximum size: 5MB per image
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center bg-gray-900 p-4">
              <div className="h-20 w-20 md:h-24 md:w-24 rounded-full bg-purple-700 flex items-center justify-center text-white mb-4 shadow-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 md:h-12 md:w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <p className="text-gray-200 text-base md:text-lg font-medium text-center">
                Select a chat to start messaging
              </p>
              <p className="text-gray-500 text-xs md:text-sm mt-2 px-4 md:px-6 text-center">
                Choose from your available chat rooms{" "}
                {isMobile ? "using the menu button" : "on the left"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
